import { KeyValuePipe, NgClass } from '@angular/common';
import { Component, NgZone, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from '@shared-angular/services/toastr.service';
import * as Blockly from 'blockly';
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { ButtonModule } from 'primeng/button';
import { TagModule } from "primeng/tag";
import { BlocklyPluginsService } from '../blockly-plugins.service';
import { CodemirrorDannComponent } from '../shared/components/ai-bot/utils/codemirror-dann/codemirror-dann.component';
import { BlocklyStorageService } from '../shared/services/blockly-storage.service';

type ToolboxItemInfo = Blockly.utils.toolbox.ToolboxItemInfo;

declare global {
  interface Window {
    RAGBOT: unknown;
    pliginFromCode: (code: string) => void;
  }
}

@Component({
  selector: 'app-blockly-plugins-drawer-content',
  imports: [NgClass, KeyValuePipe, FormsModule, TooltipModule, ButtonModule, TagModule, CodemirrorDannComponent],
  standalone: true,
  templateUrl: './blockly-plugins-drawer-content.component.html',
  styleUrl: './blockly-plugins-drawer-content.component.scss'
})
export class BlocklyPluginsDrawerContentComponent {
  public readonly pluginAdded = output<ToolboxItemInfo>();
  public readonly pluginDeleted = output<string>();
  public readonly openBottly = output();
  protected readonly pluginDeleteConfirmLabel = $localize`:@@plugin-delete-confirm:Plugin löschen?`;

  constructor(public bps: BlocklyPluginsService, private zone: NgZone, public bss: BlocklyStorageService, private toastr: ToastrService) {
    // Wir wrappen die Funktion, damit sie innerhalb der Angular-Zone läuft
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (window as any).pluginFromCode = (data: any, name: string) => {
      this.zone.run(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.pluginFromCode(data, name);
      });
    };
  }



  public async pluginDownload(pluginId: string) {
    const code = await this.bps.fetchPluginByPath(pluginId);
    const blob = new Blob([code], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.bps.getPluginNameById(pluginId) ?? 'plugin'}.block`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

  }
  // Variable für den Toggle-Zustand
  public showManualEditor = false;
  public myNewJsCode: string = "";
  public myNewPluginName: string = "";

  public pluginFromCode(code: string, name: string) {
    this.showManualEditor = true;
    this.myNewJsCode = code;
    this.myNewPluginName = name;
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    RAGBOT.closeChatbot();
  }

  public generatePluginTemplate(name: string, jsCode: string) {
    if (!name || !jsCode) {
      this.toastr.error('GENERAL', $localize`:@@plugin-name-code-missing:Bitte gib einen Namen und Code ein.`, { timeOut: 10000 });
      return;
    }

    // Beim Bearbeiten die ursprüngliche Block-ID beibehalten, damit bereits platzierte Blöcke gültig bleiben
    const idSection = this.editingBlockId ? `#::ID\n${this.editingBlockId}\n` : "";

    // Hier bauen wir das exakte Template, das dein System braucht
    const template = `@plugin:${name.trim()}

###BLOCKDEF
${idSection}
#::block
JavaSctipt Based Block
#::code
${jsCode.trim()}`;

    // Durch das Setzen von pluginCode springt das Template automatisch in die Vorschau
    this.pluginCode = template;
  }

  public pluginCode: string = "";
  public editingPluginId: string | null = null;
  public editingBlockId: string | null = null;
  public formatPlaceholder(placeholder: string): string {
    const formattedText = placeholder.replace(/{{(.*?)}}/g, "<b>{{$1}}</b>");
    return formattedText;
  }
  public removePlugin(pluginId: string) {
    void this.bps.removePluginById(pluginId);
    this.pluginDeleted.emit(pluginId);
  }
  public reverseOrder(_a: unknown, _b: unknown): number {
    return -1;
  }

  public async editPlugin(pluginId: string) {
    const pluginTXT = await this.bps.fetchPluginByPath(pluginId);
    const rawBlocks = this.bps.getRawBlocks(pluginTXT);
    this.editingPluginId = pluginId;

    if (rawBlocks.length === 1) {
      // Einzelblock-Plugins wurden über den JS-Editor erstellt, daher hier wieder Name & Code anzeigen
      const blockData = this.bps.parseBlockFileToObject(rawBlocks[0] ?? "");
      this.editingBlockId = blockData.ID || null;
      this.myNewPluginName = this.bps.getPluginName(pluginTXT, pluginId);
      this.myNewJsCode = blockData.code;
      this.pluginCode = "";
      this.showManualEditor = true;
    } else {
      // Mehrblock-Plugins lassen sich nicht im JS-Editor abbilden, daher Rohtext bearbeiten
      this.editingBlockId = null;
      this.pluginCode = pluginTXT;
      this.showManualEditor = false;
    }
  }

  public cancelEdit() {
    this.pluginCode = "";
    this.showManualEditor = false;
    this.editingPluginId = null;
    this.editingBlockId = null;
    this.myNewJsCode = "";
    this.myNewPluginName = "";
  }

  public pluginIsValid(pluginTXT: string) {
    if (pluginTXT.trim() == "") {
      this.toastr.error('GENERAL', $localize`:@@empty-plugin-code:Bitte gib einen Plugin-Code ein.`, { timeOut: 10000 });
      return false;
    }
    if (!pluginTXT.startsWith("@plugin:")) {
      this.toastr.error('GENERAL', $localize`:@@plugin-code-must-start-with:Der Plugin-Code muss mit '@plugin:' beginnen.`, { timeOut: 10000 });
      return false;
    }


    return true;
  }

  public async addPlugin() {
    const fixedPluginCode = this.bps.addBlockUniqueIdsToPluginTxt(this.pluginCode);
    if (!this.bps.pluginIsValid(fixedPluginCode)) {
      this.toastr.error('GENERAL', $localize`:@@invalid-plugin-code:Ungültiger Plugin-Code.`, { timeOut: 10000 });
      return;
    }

    const pluginCategory = this.bps.registerPluginByTxt(fixedPluginCode, this.editingPluginId);
    if (pluginCategory) {
      if (this.editingPluginId) {
        // Alten Toolbox-Eintrag entfernen, bevor die Namens-Zuordnung überschrieben wird
        const pluginId = this.editingPluginId;
        this.pluginDeleted.emit(pluginId);
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await this.bps.updatePlugin(pluginId, fixedPluginCode, pluginCategory.name);
        this.pluginAdded.emit(pluginCategory);
      } else {
        this.pluginAdded.emit(pluginCategory);
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await this.bps.addPlugin(fixedPluginCode, pluginCategory.name);
      }
    }
    this.pluginCode = "";
    this.myNewJsCode = "";
    this.myNewPluginName = "";
    this.editingPluginId = null;
    this.editingBlockId = null;
    this.showManualEditor = false;
  }

  public onFileSelected(event: Event) {

    const file: File | undefined = (event as unknown as { target: { files: File[] } }).target.files[0];
    console.log("File selected:", file);
    if (!file?.name.endsWith('.block')) {
      this.toastr.error('GENERAL', $localize`:@@only-block-files-allowed:Nur .block-Dateien sind erlaubt.`, { timeOut: 10000 });
      return;
    }
    const reader = new FileReader();



    reader.onload = (e) => {
      console.log("File content loaded", e);
      const content = e.target?.result as string;
      this.pluginCode = content;

    };
    reader.readAsText(file);
    // Reset the file input
    const inputEl = event.target;
    if (inputEl instanceof HTMLInputElement) {
      inputEl.value = '';
    }
  }




  public reset() {
    this.pluginCode = "";
    this.myNewJsCode = "";
    this.myNewPluginName = "";
    this.editingPluginId = null;
    this.editingBlockId = null;
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      // @ts-expect-error
      if (!file.name.endsWith('.block')) {
        this.toastr.error('GENERAL', $localize`:@@only-block-files-allowed:Nur .block-Dateien sind erlaubt.`, { timeOut: 10000 });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        this.pluginCode = content;
        //        this.addPlugin();
      };
      // @ts-expect-error
      reader.readAsText(file);
    }
  }

}
